// src/components/SchedulePostModal/SchedulePostModal.jsx
import React, { useState, useEffect } from "react";
import "./SchedulePostModal.css";
import { createScheduledPost } from "../../services/scheduledPostsApi";

const SchedulePostModal = ({ isOpen, onClose, postContent, onScheduled }) => {
    const [selectedDate, setSelectedDate] = useState("");
    const [selectedTime, setSelectedTime] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [showCustomTime, setShowCustomTime] = useState(false);

    // Generate common time slots (every 30 minutes from 6 AM to 11 PM)
    const generateTimeSlots = () => {
        const slots = [];
        for (let hour = 6; hour <= 23; hour++) {
            for (let minute = 0; minute < 60; minute += 30) {
                const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                const displayTime = new Date(`2000-01-01T${timeStr}`).toLocaleTimeString([], {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                });
                slots.push({ value: timeStr, label: displayTime });
            }
        }
        return slots;
    };

    const timeSlots = generateTimeSlots();

    // Quick selection presets
    const quickSelections = [
        { label: "In 1 hour", hours: 1 },
        { label: "In 3 hours", hours: 3 },
        { label: "In 6 hours", hours: 6 },
        {
            label: "Tomorrow 9 AM", custom: true, getDate: () => {
                const tomorrow = new Date();
                tomorrow.setDate(tomorrow.getDate() + 1);
                tomorrow.setHours(9, 0, 0, 0);
                return tomorrow;
            }
        },
        {
            label: "Tomorrow 2 PM", custom: true, getDate: () => {
                const tomorrow = new Date();
                tomorrow.setDate(tomorrow.getDate() + 1);
                tomorrow.setHours(14, 0, 0, 0);
                return tomorrow;
            }
        },
    ];

    // Set default date/time to 1 hour from now
    useEffect(() => {
        if (isOpen) {
            const now = new Date();
            now.setHours(now.getHours() + 1);
            now.setMinutes(0);

            const dateStr = now.toISOString().split('T')[0];
            const timeStr = now.toTimeString().slice(0, 5);

            setSelectedDate(dateStr);
            setSelectedTime(timeStr);
            setError("");
            setShowCustomTime(false);
        }
    }, [isOpen]);

    const handleQuickSelect = (preset) => {
        let targetDate;

        if (preset.custom) {
            targetDate = preset.getDate();
        } else {
            targetDate = new Date();
            targetDate.setHours(targetDate.getHours() + preset.hours);
            targetDate.setMinutes(0);
            targetDate.setSeconds(0);
        }

        const dateStr = targetDate.toISOString().split('T')[0];
        const timeStr = targetDate.toTimeString().slice(0, 5);

        setSelectedDate(dateStr);
        setSelectedTime(timeStr);
        setError("");
    };

    const handleSchedule = async () => {
        setError("");

        // Validate inputs
        if (!selectedDate || !selectedTime) {
            setError("Please select both date and time");
            return;
        }

        // Combine date and time into a Date object
        const scheduledDateTime = new Date(`${selectedDate}T${selectedTime}`);

        // Validate it's in the future
        const now = new Date();
        if (scheduledDateTime <= now) {
            setError("Scheduled time must be in the future");
            return;
        }

        // Validate it's not too far in the future (1 year)
        const maxFuture = new Date();
        maxFuture.setFullYear(maxFuture.getFullYear() + 1);
        if (scheduledDateTime > maxFuture) {
            setError("Scheduled time cannot be more than 1 year in the future");
            return;
        }

        setLoading(true);

        try {
            const result = await createScheduledPost({
                content: postContent,
                scheduled_at: scheduledDateTime,
            });

            // Success!
            if (onScheduled) {
                onScheduled(result);
            }
            onClose();
        } catch (err) {
            setError(err.message || "Failed to schedule post");
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="schedule-modal-overlay" onClick={onClose}>
            <div className="schedule-modal" onClick={(e) => e.stopPropagation()}>
                <div className="schedule-modal-header">
                    <h2>Schedule Post</h2>
                    <button className="close-btn" onClick={onClose}>
                        ×
                    </button>
                </div>

                <div className="schedule-modal-body">
                    <p className="schedule-description">
                        Choose when you want this post to be published on LinkedIn.
                    </p>

                    {/* Quick Selection Buttons */}
                    <div className="quick-selections">
                        <p className="quick-selections-label">Quick select:</p>
                        <div className="quick-selection-buttons">
                            {quickSelections.map((preset, index) => (
                                <button
                                    key={index}
                                    type="button"
                                    className="quick-select-btn"
                                    onClick={() => handleQuickSelect(preset)}
                                >
                                    {preset.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="divider">
                        <span>or choose custom time</span>
                    </div>

                    <div className="datetime-inputs">
                        <div className="input-group">
                            <label>Date</label>
                            <input
                                type="date"
                                value={selectedDate}
                                onChange={(e) => setSelectedDate(e.target.value)}
                                min={new Date().toISOString().split('T')[0]}
                            />
                        </div>

                        <div className="input-group">
                            <label>Time</label>
                            {!showCustomTime ? (
                                <div className="time-selector-wrapper">
                                    <select
                                        className="time-select"
                                        value={selectedTime}
                                        onChange={(e) => setSelectedTime(e.target.value)}
                                    >
                                        {timeSlots.map((slot) => (
                                            <option key={slot.value} value={slot.value}>
                                                {slot.label}
                                            </option>
                                        ))}
                                    </select>
                                    <button
                                        type="button"
                                        className="custom-time-btn"
                                        onClick={() => setShowCustomTime(true)}
                                    >
                                        Custom time
                                    </button>
                                </div>
                            ) : (
                                <div className="time-selector-wrapper">
                                    <input
                                        type="time"
                                        value={selectedTime}
                                        onChange={(e) => setSelectedTime(e.target.value)}
                                        className="custom-time-input"
                                    />
                                    <button
                                        type="button"
                                        className="custom-time-btn"
                                        onClick={() => setShowCustomTime(false)}
                                    >
                                        Use presets
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {selectedDate && selectedTime && (
                        <div className="scheduled-preview">
                            <strong>Scheduled for:</strong>{" "}
                            {new Date(`${selectedDate}T${selectedTime}`).toLocaleString(undefined, {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                            })}
                        </div>
                    )}

                    {error && <div className="error-message">{error}</div>}
                </div>

                <div className="schedule-modal-footer">
                    <button className="cancel-btn" onClick={onClose} disabled={loading}>
                        Cancel
                    </button>
                    <button
                        className="schedule-btn"
                        onClick={handleSchedule}
                        disabled={loading}
                    >
                        {loading ? "Scheduling..." : "Schedule Post"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SchedulePostModal;
